/**
 * Раскладка печатного листа в миллиметрах (D64).
 *
 * Координаты сняты с образца — авторского бланка, свёрстанного
 * в Microsoft Publisher: страница A4, 210 × 297 мм, рамки толщиной
 * 2 пункта с внутренним контуром в 0.75 пункта и скосом углов около
 * 3 мм. Числа стоят здесь, а не в CSS: по ним тест проверяет, что
 * ни одна рамка не выходит за поля страницы, — лист обязан печататься
 * ровно на одной странице.
 *
 * `[x, y, ширина, высота]` от левого верхнего угла листа.
 */

export type Box = readonly [x: number, y: number, w: number, h: number]

export const PAGE = { width: 210, height: 297 } as const

/**
 * Поля печати. Домашний принтер не печатает у самого края — обычно
 * 3–5 мм, — поэтому ничего ближе 7 мм к краю листа не ставится.
 */
export const PRINT_MARGIN = 7

/** Шапка: лента с именем и рамка с полями персонажа. */
export const HEADER = {
  nameBanner: [16.9, 13.5, 70.4, 18] as Box,
  nameLabel: [16.9, 32.5, 70.4, 3.5] as Box,
  info: [87.3, 11.5, 98.3, 29] as Box,
} as const

/**
 * Поля шапки: три строки по три поля. В образце строк две; третья —
 * под подкласс, черту происхождения и код персонажа, которых в образце
 * нет. Линия — та, на которой стоит значение, подпись — под ней.
 */
export const HEADER_GRID = {
  columns: [90.5, 122, 153.5] as const,
  columnWidth: 29.5,
  lines: [18.6, 27.4, 36.2] as const,
} as const

/** Левый столбец: бонусы и шесть характеристик со спасбросками и навыками. */
export const LEFT = {
  frame: [16.6, 43, 71.8, 193] as Box,
  /** Три строки над характеристиками: бонус мастерства, вдохновение, пассивное. */
  rows: [47, 58.9, 70.9] as const,
  rowValue: { x: 20, w: 19, h: 10.1 },
  rowLabel: { x: 37.4, w: 47, h: 7.4 },
  /** Верх рамки каждой характеристики; шаг — 25.3 мм, как в образце. */
  abilityTops: [85, 110.3, 135.6, 160.9, 186.2, 211.5] as const,
  abilityBox: { x: 20.2, w: 23.6, h: 20.4 },
  /** Столбец спасброска и навыков справа от рамки характеристики. */
  skills: { x: 46, w: 40, row: 3.75 },
} as const

/** Правый верх: КД, инициатива, скорость, хиты, кость хитов, спасброски от смерти. */
export const COMBAT = {
  frame: [90.1, 43, 101.1, 87] as Box,
  person: [88.9, 39.7, 19.6, 19.6] as Box,
  ac: [112, 45.5, 21, 23] as Box,
  initiative: [137.1, 46.9, 22, 20] as Box,
  speed: [164.6, 47, 22, 20] as Box,
  hp: [95.3, 71, 60.5, 36.6] as Box,
  heart: [93.1, 66.3, 16, 16] as Box,
  hitDice: [158.5, 70.8, 27.8, 36.8] as Box,
  deathSaves: [97.7, 113.6, 86.1, 7.2] as Box,
  deathLabel: [97.7, 122.2, 86.1, 3.5] as Box,
} as const

/** Атаки: шесть строк, как в образце. */
export const ATTACKS = {
  frame: [90.1, 132.3, 100.7, 54.4] as Box,
  swords: [88.9, 131.1, 19.6, 19.6] as Box,
  title: [110, 135.2, 76, 6] as Box,
  /** Таблица начинается под кругом с мечами, как у образца. */
  table: [93, 146.4, 94, 37.8] as Box,
} as const

/** Строка заголовка таблицы атак и строки под атаки. */
export const ATTACK_TABLE = {
  head: 4.2,
  row: 5.6,
  /**
   * Столбцы. Столбца дистанции, как в образце, нет: дальностей
   * приложение не ведёт, а ближний или дальний бой стоит мелко
   * под названием.
   */
  columns: {
    name: { x: 93, w: 40 },
    bonus: { x: 133, w: 14 },
    damage: { x: 147, w: 15 },
    type: { x: 162, w: 25 },
  },
} as const

/** Числа заклинателя. */
export const SPELLS = {
  frame: [90.2, 188.6, 63.2, 47.9] as Box,
  rows: [192.8, 206.5, 220] as const,
  label: { x: 92.5, w: 31.5 },
  value: { x: 125.1, w: 23.5, h: 12.4 },
} as const

/** Деньги: пять монет столбцом. */
export const COINS = {
  frame: [155.2, 188.6, 36, 86] as Box,
  icon: [163.3, 186.6, 19.6, 19.6] as Box,
  rows: [208.2, 221.5, 233.9, 246.4, 258.9] as const,
  tab: { x: 157.2, w: 8.2, h: 6.4 },
  value: { x: 164.8, w: 22.8, h: 10.6 },
} as const

/**
 * Нижняя рамка. В образце она пустая, под заметки; у нас здесь
 * счётчики ресурсов — то, что за столом вычёркивают карандашом.
 */
export const BOTTOM = {
  frame: [16.2, 238.2, 137.4, 36.4] as Box,
  title: [20, 240, 60, 3.6] as Box,
  /** Два столбца по пять счётчиков; свободный столбец — под заметки. */
  columns: [19.8, 86.6] as const,
  columnWidth: 64,
  firstRow: 244.4,
  row: 5.7,
} as const

/** Строка счётчика внутри столбца: отступы от левого края столбца. */
export const RESOURCE_ROW = {
  name: { x: 0, w: 34 },
  current: { x: 35, w: 8, h: 4.5 },
  max: { x: 44, w: 6.5 },
  ticks: { x: 51, size: 1.9, gap: 0.3, most: 6 },
} as const

/** Подвал: указание источника SRD и иконок — лист уходит из приложения. */
export const FOOTER = [16.2, 278, 175, 10] as Box

/** Все рамки разом: по ним тест сверяет поля страницы. */
export const ALL_BOXES: Readonly<Record<string, Box>> = {
  ...prefixed('header', HEADER),
  'left.frame': LEFT.frame,
  ...prefixed('combat', COMBAT),
  ...prefixed('attacks', ATTACKS),
  'spells.frame': SPELLS.frame,
  'coins.frame': COINS.frame,
  'coins.icon': COINS.icon,
  'bottom.frame': BOTTOM.frame,
  footer: FOOTER,
}

function prefixed(prefix: string, boxes: Readonly<Record<string, Box>>) {
  return Object.fromEntries(
    Object.entries(boxes).map(([key, box]) => [`${prefix}.${key}`, box]),
  )
}

/** `style` абсолютно поставленного блока. */
export function at([x, y, w, h]: Box): {
  left: string
  top: string
  width: string
  height: string
} {
  return { left: `${x}mm`, top: `${y}mm`, width: `${w}mm`, height: `${h}mm` }
}
